// src/components/quizzes/kit/QuizKit.js
//
// Shared pieces of the new question games (MultipleChoice, DragDropQuiz):
//
//   <QuizHud title index total score streak muted onMute />
//        Leave · topic + "Question 3 of 10" bar · ⭐ score · 🔥 streak · 🔊
//   useGameMusic(url)      → [muted, toggleMute, duck(on)]
//   useReadAloud()         → { read(parts), stop, reading, partIndex }
//        parts = [{ key, text }]; reads them one after another and tells you
//        which one is being read (to light it up). It speaks through
//        window.speechSynthesis, which services/cloudVoice.js turns into the
//        realistic cloud voice. preload(parts) gets them ready ahead of time.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLeaveGame } from "../gameExit";
import { prefetchSpeech } from "../../../services/cloudVoice";
import "./quizkit.css";

export const useGameMusic = (url, volume = 0.25) => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(() => {
    try {
      return localStorage.getItem("sbl-game-muted") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!url) return undefined;
    const a = new Audio(url);
    a.loop = true;
    a.volume = volume;
    a.muted = muted;
    ref.current = a;
    a.play().catch(() => {
      // autoplay blocked: start on the first tap
      const go = () => a.play().catch(() => {});
      window.addEventListener("pointerdown", go, { once: true });
    });
    return () => {
      a.pause();
      ref.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
  const toggle = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      if (ref.current) ref.current.muted = next;
      try {
        localStorage.setItem("sbl-game-muted", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);
  const duck = useCallback(
    (on) => {
      if (ref.current) ref.current.volume = on ? volume * 0.25 : volume;
    },
    [volume]
  );
  return [muted, toggle, duck];
};

// What each part is, for the admin's speeds (services/cloudVoice.js):
// key "q" is the question, everything else an answer / card, unless a part
// says { kind } itself.
const kindOf = (p) => p.kind || (p.key === "q" ? "question" : "answer");
// What is actually spoken for a part. A part can split its words with
// { say: ["A.", "Earth"] } so each piece is made once and reused: answers
// are shuffled, so "A. Earth" and "C. Earth" would otherwise be two sentences
// to make, and neither ready the first time.
const pieces = (p) => (Array.isArray(p.say) && p.say.length ? p.say : [p.text]).filter((t) => t && /[a-z0-9]/i.test(String(t)));

export const useReadAloud = (onDuck) => {
  const [partIndex, setPartIndex] = useState(null); // which part is being read
  const session = useRef(0);
  const can = typeof window !== "undefined" && "speechSynthesis" in window;

  const stop = useCallback(() => {
    session.current += 1;
    setPartIndex(null);
    onDuck?.(false);
    if (can) window.speechSynthesis.cancel();
  }, [can, onDuck]);

  /** Get these parts' voice ready now, so 🔊 starts at once. */
  const preload = useCallback((parts) => {
    prefetchSpeech((parts || []).filter(Boolean).flatMap(pieces));
  }, []);

  const read = useCallback(
    (parts) => {
      if (!can || !parts?.length) return;
      stop();
      const my = session.current;
      const list = parts.filter((p) => p && pieces(p).length);
      prefetchSpeech(list.flatMap(pieces));
      onDuck?.(true);
      const step = (i, j = 0) => {
        if (my !== session.current) return;
        if (i >= list.length) {
          setPartIndex(null);
          onDuck?.(false);
          return;
        }
        const say = pieces(list[i]);
        if (j >= say.length) {
          step(i + 1, 0);
          return;
        }
        if (j === 0) setPartIndex(parts.indexOf(list[i]));
        const u = new SpeechSynthesisUtterance(say[j]);
        u.lang = "en-US";
        u.sblKind = kindOf(list[i]); // speed comes from the admin settings
        const next = () => step(i, j + 1);
        u.onend = next;
        u.onerror = (e) => {
          if (e?.error === "interrupted" || e?.error === "canceled") return;
          next();
        };
        window.speechSynthesis.speak(u);
      };
      // a tick between cancel() and speak() (Chrome drops a speak issued in
      // the same tick as a cancel)
      setTimeout(() => step(0), 30);
    },
    [can, stop, onDuck]
  );

  useEffect(() => () => stop(), [stop]);
  return { read, stop, preload, partIndex, reading: partIndex !== null, can };
};

export const QuizHud = ({ title, index, total, score, streak = 0, muted, onMute, scoreLabel = "" }) => {
  const leaveGame = useLeaveGame();
  const pct = total ? Math.round((Math.min(index, total) / total) * 100) : 0;
  return (
    <header className="qk-hud">
      <button type="button" className="qk-leave" onClick={() => leaveGame()} aria-label="Leave the game">
        ← Leave
      </button>
      <div className="qk-progress">
        <div className="qk-progress-top">
          <span className="qk-topic">{title}</span>
          <span className="qk-count">
            Question <b>{Math.min(index + 1, total)}</b> of {total}
          </span>
        </div>
        {total > 0 && total <= 12 ? (
          // one step per question: done ones filled, this one glowing
          <div className="qk-steps" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={index}>
            {Array.from({ length: total }, (_, n) => (
              <i key={n} className={n < index ? "is-done" : n === index ? "is-now" : ""} />
            ))}
          </div>
        ) : (
          <div className="qk-bar" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={index}>
            <i style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
      <div className="qk-chips">
        <span className="qk-chip" title="Score">
          ⭐ {score}
          {scoreLabel}
        </span>
        {streak >= 2 && (
          <span className="qk-chip is-streak" key={streak}>
            🔥 {streak} in a row!
          </span>
        )}
        {onMute && (
          <button type="button" className="qk-icon-btn" onClick={onMute} aria-label={muted ? "Turn music on" : "Turn music off"} title={muted ? "Music off" : "Music on"}>
            {muted ? "🔇" : "🎵"}
          </button>
        )}
      </div>
    </header>
  );
};

export const LETTERS = ["A", "B", "C", "D", "E", "F"];
